import axios from 'axios'
import { Toast } from 'vant'

const service = axios.create({
	baseURL: '',
	timeout: 60000
})

const SKEW = 60 * 1000
let _refreshing = null
let _accessExpiresAt = 0  // in-memory only; HttpOnly cookies are browser-managed

function saveTokens(res) {
	if (!res) return
	// Cookies are set by the server; only track expiry in memory for proactive refresh
	if (res.expires_in) {
		_accessExpiresAt = Date.now() + res.expires_in * 1000
	}
}

function _clearSession() {
	_accessExpiresAt = 0
	// 登录时写入的 username/login_status 也要清掉，否则 access token 已失效但
	// localStorage 仍留着旧值，会让 layout/index.vue 等处的"是否已登录"判断产生假阳性。
	localStorage.removeItem('username')
	localStorage.removeItem('login_status')
	// wx_openid 也是登录时落地、之后被支付流程读取的身份信息（subscription/index.vue），
	// 残留会导致共用浏览器时下一个用户复用上一个人的 openid 去支付。
	localStorage.removeItem('wx_openid')
	// Cannot clear HttpOnly cookies from JS; server must clear them on logout
	window.location.href = '/login'
}

function doRefresh() {
	if (!_refreshing) {
		// Refresh cookie is path-restricted to /api/token/refresh/ and sent automatically
		_refreshing = service
			.post('/api/token/refresh/', {}, { _isRefresh: true })
			.then(res => {
				// 只有服务端【明确判定会话失效】(族已撤销/无效)才登出；
				// 网络错误/超时属瞬时故障,不清会话、保留 cookie 供下次重试,避免抖动误登出。
				if (res && (res.login_status === false || res.status_code === '20003' || res.result === 'failed')) {
					_clearSession()
					return Promise.reject(new Error('session revoked'))
				}
				return res
			})
			.finally(() => { _refreshing = null })
	}
	return _refreshing
}

// strict：过期时间未知（_accessExpiresAt 为 0，如启动时 refresh 失败或响应未带 expires_in）时也续期。
// 给 SSE 这类不走 axios、拿不到 20006 兜底的请求用；普通接口保持默认，靠 20006 被动刷新。
export function ensureFreshToken({ strict = false } = {}) {
	// If no active session in memory yet, skip proactive refresh (reactive 20006 handles it)
	if (_accessExpiresAt === 0) return strict ? doRefresh() : Promise.resolve()
	if (Date.now() < _accessExpiresAt - SKEW) return Promise.resolve()
	return doRefresh()
}

// 供 SSE 收到鉴权失败事件后被动续期：会话被撤销时同样会清会话并跳 /login
export function refreshSession() {
	return doRefresh()
}

// request interceptor
service.interceptors.request.use(
	async config => {
		config.withCredentials = true  // send auth cookies automatically
		config.xsrfCookieName = 'csrftoken'
		config.xsrfHeaderName = 'X-CSRFToken'
		const regex = /.*csrftoken=([^;.]*).*$/
		const m = document.cookie.match(regex)
		config.headers['X-CSRFToken'] = m ? m[1] : null
		if (!config._isRefresh) await ensureFreshToken()
		// No Authorization header — browser sends access_token cookie automatically
		return config
	},
	error => {
		console.log(error)
		return Promise.reject(error)
	}
)

// response interceptor
service.interceptors.response.use(
	response => {
		const res = response.data
		const _url = (response.config && response.config.url) || ''
		// Track expiry from auth endpoints; cookies are managed by the browser
		if (_url.includes('ainvest_login') || _url.includes('token/refresh')) {
			saveTokens(res)
		}
		if (!res) {
			Toast.fail('网络错误')
			return Promise.reject(new Error('empty response'))
		}
		// _silent：调用方自己承担"未登录"这个结果（如 judge_session 这种任何人都会调的检查型
		// 接口），不走这里的自动刷新+失败即跳转 /login——否则从未登录过的匿名访客也会被强制跳转。
		if ((res.code === 20006 || res.status_code === '20006') && !response.config._isRefresh && !response.config._retried && !response.config._silent) {
			return doRefresh().then(() => {
				const rc = { ...response.config, _retried: true }
				return service(rc)
			}).catch(() => Promise.reject(new Error('login expired')))
		}
		return res
	},
	error => {
		console.log('err' + error)
		return Promise.reject(error)
	}
)

// Call on app startup to restore session from the refresh_token cookie.
// Does NOT call doRefresh() because doRefresh's .catch redirects to /login —
// which would force-redirect first-time visitors who simply aren't logged in yet.
export async function initAuth() {
	try {
		await service.post('/api/token/refresh/', {}, { _isRefresh: true })
	} catch {
		// No valid refresh cookie; visitor is not logged in — that's fine.
	}
}

export default service
